import {
  Array,
  Literal,
  Null,
  Record as RRecord,
  Static,
  String,
  Union,
} from "runtypes";

import { EnumValues, OrgRole } from "./enums";
import { isOrgRoleSuperset } from "./orgRole";
import { getNormalEnum } from "./runtypeEnums";
import {
  PartialUserInteractions,
  UserInteraction,
} from "./userInteractionTypes";

export const OnboardingTypeLiteral = Union(
  Literal("TRIAL"),
  Literal("CANDIDATE"),
  Literal("FRIEND"),
);

export type OnboardingType = Static<typeof OnboardingTypeLiteral>;
export const OnboardingType = getNormalEnum(OnboardingTypeLiteral);

export const LANGUAGE_SKIPPED = "SKIPPED";

export type LanguageSkipped = typeof LANGUAGE_SKIPPED;

export const LanguagePreferenceLiteral = Union(
  Literal("SQL"),
  Literal("SQL_AND_PYTHON"),
  Literal("NO_CODE"),
);

export const LanguagePreferenceSource = {
  ONBOARDING: "ONBOARDING",
} as const;
export type LanguagePreferenceSource = EnumValues<
  typeof LanguagePreferenceSource
>;

export const LanguagePreference = getNormalEnum(LanguagePreferenceLiteral);

export type LanguagePreference = Static<typeof LanguagePreferenceLiteral>;

export const UserLanguagePreference = {
  ...LanguagePreference,
  SKIPPED: LANGUAGE_SKIPPED,
} as const;

export type UserLanguagePreference = EnumValues<typeof UserLanguagePreference>;

export const UseCaseLiteral = Union(
  Literal("EDA"),
  Literal("INTERACTIVE_APPS"),
  Literal("SELF_SERVE"),
  Literal("EMBEDDED_APPS"),
  Literal("OTHER"),
  // older values (pre 2025 questionnaire changes)
  Literal("BI"),
  Literal("INTERNAL_TOOLS"),
  Literal("ML"),
  Literal("DATA_PIPELINING"),
);

export const UseCase = getNormalEnum(UseCaseLiteral);
export type UseCase = Static<typeof UseCaseLiteral>;

export const CollaborationIntentLiteral = Union(
  Literal("JUST_ME"),
  Literal("WITH_A_TEAM"),
);

export type CollaborationIntent = Static<typeof CollaborationIntentLiteral>;
export const CollaborationIntent = getNormalEnum(CollaborationIntentLiteral);

export const WorkspaceIntentLiteral = Union(
  Literal("JOB"),
  Literal("SCHOOL"),
  Literal("PERSONAL"),
);

export const CompanySizeLiteral = Union(
  Literal("ONE_TO_TEN"),
  Literal("ELEVEN_TO_FIFTY"),
  Literal("FIFTY_ONE_TO_TWO_HUNDRED_AND_FIFTY"),
  Literal("TWO_HUNDRED_AND_FIFTY_ONE_TO_ONE_THOUSAND"),
  Literal("ONE_THOUSAND_PLUS"),
);
export type CompanySizeLiteral = Static<typeof CompanySizeLiteral>;

export const WorkspaceIntent = getNormalEnum(WorkspaceIntentLiteral);
export type WorkspaceIntent = Static<typeof WorkspaceIntentLiteral>;

export const PersonaTypeOfWorkLiteral = Union(
  Literal("DATA_SCIENCE"),
  Literal("BUSINESS_INTELLIGENCE"),
  Literal("ENGINEERING"),
  Literal("PRODUCT_MANAGEMENT"),
  Literal("INFORMATION_TECHNOLOGY"),
  Literal("OPERATIONS"),
  Literal("SALES"),
  Literal("MARKETING"),
  Literal("FINANCE"),
  Literal("CUSTOMER_SERVICE_OR_SUPPORT"),
  Literal("OTHER"),
);
export type PersonaTypeOfWork = Static<typeof PersonaTypeOfWorkLiteral>;

export const PersonaRoleLiteral = Union(
  Literal("NON_MANAGER"),
  Literal("MANAGER"),
  Literal("DIRECTOR"),
  Literal("VP_LEVEL"),
  Literal("C_LEVEL"),
  Literal("CONSULTANT_OR_CONTRACTOR"),
);
export type PersonaRole = Static<typeof PersonaRoleLiteral>;

export const UserLanguagePreferencesRawLiteral = Union(
  Literal("PYTHON"),
  Literal("SQL"),
  Literal("NO_CODE"),
);
export type UserLanguagePreferencesRaw = Static<
  typeof UserLanguagePreferencesRawLiteral
>;

export const Persona = RRecord({
  // Pre 2025 onboarding questionnaires
  useCases: Array(UseCaseLiteral).optional(),
  useCaseOtherValues: Array(String).optional(),

  // Onboarding questionnaire revamp 2025
  typeOfWork: PersonaTypeOfWorkLiteral.optional(),
  role: PersonaRoleLiteral.optional(),
  // Pre and post 2025 onboarding questions
  language: LanguagePreferenceLiteral.Or(Null).Or(Literal(LANGUAGE_SKIPPED)),
  rawLanguages: Array(UserLanguagePreferencesRawLiteral).optional(),
});
export type Persona = Static<typeof Persona>;

export const PersonaDefault = {
  language: null,
  rawLanguages: [],
};

// Only show onboarding to users created after this date.
// This is because in https://github.com/hex-inc/hex/pull/18263,
// we intentionally simplified some logic around onboarding causing a side effect where old users
// who hadn't previously might now see onboarding. To prevent that, we just check that users were created
// near or after that change.
const CREATOR_ONBOARDING_CUTOFF = new Date("2023-10-23T00:00:00Z");

const NEW_CREATOR_ONBOARDING_CUTOFF = new Date("2025-11-01T00:00:00Z");

interface UserFields {
  createdDate: Date;
  orgRole: OrgRole;
  persona: Persona | null;
  interactions: PartialUserInteractions | null;
}
export const showCreatorOnboarding = (
  { createdDate, interactions, orgRole, persona }: UserFields,
  useNewOnboarding: boolean,
): boolean => {
  if (useNewOnboarding) {
    const hasSeenNewCreatorOnboarding = interactions
      ? interactions[UserInteraction.CREATOR_ONBOARDING_COMPLETED]
      : false;
    return (
      createdDate > NEW_CREATOR_ONBOARDING_CUTOFF &&
      isOrgRoleSuperset(orgRole, OrgRole.EXPLORER) &&
      !hasSeenNewCreatorOnboarding
    );
  } else {
    return (
      createdDate > CREATOR_ONBOARDING_CUTOFF &&
      isOrgRoleSuperset(orgRole, OrgRole.EDITOR) &&
      !!persona &&
      persona.language == null
    );
  }
};

export const convertRawLanguageOptionsToUserLanguagePreference = (
  rawLanguages: Set<UserLanguagePreferencesRaw>,
): Exclude<UserLanguagePreference, "SKIPPED"> => {
  let language: UserLanguagePreference = UserLanguagePreference.NO_CODE;
  if (rawLanguages.has("PYTHON")) {
    language = UserLanguagePreference.SQL_AND_PYTHON;
  } else if (rawLanguages.has("SQL")) {
    language = UserLanguagePreference.SQL;
  }
  return language;
};
